/**
 * InsightsPanel Component
 * v4.25 - 2026-10-05 - SCREENS-301: upgrade subscription and pay-per-question checkout POSTs
 *         routed through the authenticated axios client so the Clerk bearer attaches; raw fetch
 *         sent no auth and the backend 401'd (and the api client's response interceptor would
 *         then kick the user to /login, which is why the dialog closed without error).
 * v3.91 - 2026-07-02 - F4/F5: TYPE_CONFIG + IMPACT_COLORS moved to insightTypeConfig.tsx (§3.3)
 * v3.90 - 2026-07-02 - W3-1-Step3: WOW insight amber treatment + wowSourceChunks prop to ShowYourWork
 * v3.88 - 2026-06-10 - STORY-10E-C: catalog-driven upgrade tier_id and price; no 'monthly_499'/literal
 * v3.87 - 2026-03-16 - Fix 1: Removed "See other options" link from Screen 1. Screen 2
 *                      (pay-per-question) now appears ONLY on X-dismiss of Screen 1 and
 *                      ONLY when PAY_PER_QUESTION_ENABLED flag is true (returned by /recommendations).
 * v3.86 - 2026-03-16 - Story 19 PLG: Lock overlay for free:false insights (no blur — numbers
 *                      never reach the browser). Upgrade modal (Stripe checkout). Pay-per-question
 *                      rejection path gated by PAY_PER_QUESTION_ENABLED feature flag.
 * v3.77 - 2026-03-13 - SHOW-01: Added ShowYourWork panel below each recommendation card
 * v3.29 - 2026-02-22 - Confidence label + tooltip; inline report dialog instead of page navigation
 * v3.28 - 2026-02-22 - Add confidence %, clickable drill-down to related KPI card
 * v3.19 - 2026-02-17 - AI-powered prescriptive recommendations panel
 *
 * Displays actionable recommendations from the pipeline ML analysis.
 * Type-based icons/colors, impact chips, confidence %, dollar value labels, expandable cards.
 * Returns null when no recommendations exist (panel hidden entirely).
 * free:false cards show plain text + lock icon overlay — numbers were already redacted server-side.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Skeleton,
  Chip,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  Lightbulb as LightbulbIcon,
  ExpandMore as ExpandMoreIcon,
  Assessment as AssessmentIcon,
  Lock as LockIcon,
  RocketLaunch as RocketLaunchIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api, {
  dashboardKPIService,
  reportService,
  plansService,
  Recommendation,
  Report,
  Tier,
} from '../services/api';
import ReportViewer from './ReportViewer';
import ShowYourWork from './ShowYourWork';
// v3.88 - STORY-10E-C: catalog-driven upgrade logic
import { findUpgradeTier } from '../utils/planCatalog';
// v3.90 - W3-1-Step3
import { WOW_INSIGHTS_ENABLED } from '../config/featureFlags';
// v3.91 - F4/F5: TYPE_CONFIG + IMPACT_COLORS moved to config (§3.3)
import { TYPE_CONFIG, IMPACT_COLORS } from '../config/insightTypeConfig';

const InsightsPanel: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // v3.29 - Inline report dialog state
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [reports, setReports] = useState<Report[]>([]);
  const [reportsLoaded, setReportsLoaded] = useState(false);

  // v3.86 - Story 19 PLG: upgrade modal + pay-per-question rejection path
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  // v3.88 - STORY-10E-C: catalog-driven upgrade tier (populated lazily on first modal open)
  const [upgradeCatalogTier, setUpgradeCatalogTier] = useState<Tier | null>(null);
  // v3.87 - payPerQuestionEnabled: populated from /recommendations response.
  // Screen 2 ONLY shows when this is true AND user dismisses Screen 1 via X button.
  const [payPerQuestionEnabled, setPayPerQuestionEnabled] = useState(false);
  const [showPayPerQuestion, setShowPayPerQuestion] = useState(false);
  const [payPerQuestionLoading, setPayPerQuestionLoading] = useState(false);
  const [lockedInsightId, setLockedInsightId] = useState<string | null>(null);

  const handleOpenUpgrade = (insightId: string) => {
    setLockedInsightId(insightId);
    setShowPayPerQuestion(false);
    setUpgradeModalOpen(true);
    // v3.88 - lazy-load upgrade tier from catalog on first open
    if (!upgradeCatalogTier) {
      plansService.getPlans().then((tiers) => setUpgradeCatalogTier(findUpgradeTier(tiers))).catch(() => {});
    }
  };

  // v3.87 - Fix 1: X-button handler. Screen 2 triggered here, not by any visible link.
  // If closing Screen 1 and flag is on → pivot to Screen 2.
  // If closing Screen 2 (or flag is off) → close cleanly.
  const handleDialogClose = () => {
    if (!showPayPerQuestion && payPerQuestionEnabled) {
      // Pivot to Screen 2 — do NOT close the dialog
      setShowPayPerQuestion(true);
    } else {
      // Clean close
      setUpgradeModalOpen(false);
      setShowPayPerQuestion(false);
    }
  };

  // v4.25 - SCREENS-301: subscription checkout POST routed via the authenticated axios client. - 2026-10-05
  const handleUpgradeSubscription = async () => {
    setUpgradeLoading(true);
    try {
      let tierForCheckout = upgradeCatalogTier;
      if (!tierForCheckout) {
        const tiers = await plansService.getPlans();
        tierForCheckout = findUpgradeTier(tiers);
        setUpgradeCatalogTier(tierForCheckout);
      }
      if (!tierForCheckout) { setUpgradeLoading(false); return; }
      const res = await api.post<{ checkout_url?: string; checkoutUrl?: string }>(
        '/billing/checkout',
        { plan: tierForCheckout.tier_id }
      );
      const url = res.data?.checkout_url || res.data?.checkoutUrl;
      if (url) window.location.href = url;
    } catch (err) {
      console.error('v4.25: upgrade checkout failed', err);
    } finally {
      setUpgradeLoading(false);
    }
  };

  // v4.25 - SCREENS-301: pay-per-question checkout POST routed via the authenticated axios client.
  //         axios throws on non-2xx, so the "not yet configured" silent-hide branch now reads
  //         err.response.data.detail (prior raw-fetch read it from the 2xx-coerced body). - 2026-10-05
  const handlePayPerQuestion = async () => {
    setPayPerQuestionLoading(true);
    try {
      const res = await api.post<{ checkout_url?: string; checkoutUrl?: string; detail?: string }>(
        '/billing/checkout',
        { plan: 'insight_unlock', insight_id: lockedInsightId }
      );
      const data = res.data;
      const url = data?.checkout_url || data?.checkoutUrl;
      if (url) {
        window.location.href = url;
      } else if (typeof data?.detail === 'string' && data.detail.includes('not yet configured')) {
        // STRIPE_INSIGHT_PRICE_ID not set — hide the option silently (2xx path, preserved)
        setShowPayPerQuestion(false);
        setUpgradeModalOpen(false);
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string' && detail.includes('not yet configured')) {
        // STRIPE_INSIGHT_PRICE_ID not set — hide the option silently (non-2xx path, preserved)
        setShowPayPerQuestion(false);
        setUpgradeModalOpen(false);
      } else {
        console.error('v4.25: pay-per-question checkout failed', err);
      }
    } finally {
      setPayPerQuestionLoading(false);
    }
  };

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        const response = await dashboardKPIService.getRecommendations();
        if (response.success && response.recommendations) {
          setRecommendations(response.recommendations);
        }
        // v3.87 - Fix 1: read flag from response so X-dismiss can conditionally show Screen 2
        if (response.payPerQuestionEnabled === true) {
          setPayPerQuestionEnabled(true);
        }
      } catch (err) {
        // Silent fail — panel just won't show
      } finally {
        setLoading(false);
      }
    };
    fetchRecommendations();
  }, []);

  // v3.29 - Lazy-load reports list (only once, when first drill-down is clicked)
  const ensureReportsLoaded = async (): Promise<Report[]> => {
    if (reportsLoaded) return reports;
    try {
      const response = await reportService.getAll();
      const loaded = response.reports || [];
      setReports(loaded);
      setReportsLoaded(true);
      return loaded;
    } catch (err) {
      setReportsLoaded(true);
      return [];
    }
  };

  // v3.29 - Find matching report for a recommendation
  const findMatchingReport = (rec: Recommendation, allReports: Report[]): Report | null => {
    // Priority 1: Match by relatedKpiCardId
    if (rec.relatedKpiCardId) {
      const match = allReports.find(r => r.relatedKpiCardId === rec.relatedKpiCardId);
      if (match) return match;
    }
    // Priority 2: Match by category
    if (rec.category) {
      const match = allReports.find(r => r.category === rec.category);
      if (match) return match;
    }
    // Priority 3: First available auto-generated report
    const autoReport = allReports.find(r => r.origin === 'auto' || !r.origin);
    return autoReport || null;
  };

  // v3.29 - Handle drill-down click
  const handleDrillDown = async (e: React.MouseEvent, rec: Recommendation) => {
    e.stopPropagation();
    const allReports = await ensureReportsLoaded();
    const matched = findMatchingReport(rec, allReports);
    if (matched) {
      setSelectedReport(matched);
      setReportDialogOpen(true);
    } else {
      // Fallback: navigate to Reports page if no matching report
      navigate('/reports');
    }
  };

  // Loading skeletons
  if (loading) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          AI Insights
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" width={320} height={120} />
          ))}
        </Box>
      </Box>
    );
  }

  // v3.89 - PORTAL-EMPTY-01: empty-state instead of null when no recommendations
  if (recommendations.length === 0) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          AI Insights
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 5,
            px: 2,
            textAlign: 'center',
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <LightbulbIcon sx={{ fontSize: 48, color: 'text.disabled', opacity: 0.4, mb: 1.5 }} />
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
            No insights yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete your data pipeline setup to generate AI recommendations.
          </Typography>
        </Box>
      </Box>
    );
  }

  const handleToggle = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        AI Insights
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {recommendations.map((rec) => {
          const config = TYPE_CONFIG[rec.type] || TYPE_CONFIG.insight;
          const isExpanded = expandedId === rec.id;
          // v3.86 - free field from server: undefined = treat as free (non-demo tenants)
          const isLocked = rec.free === false;
          // v3.90 - W3-1-Step3: WOW visual treatment gated by flag + RAG signal
          const isWow = WOW_INSIGHTS_ENABLED && rec.isWow === true && (rec.hierarchyTier ?? 4) <= 2;

          return (
            <Card
              key={rec.id}
              sx={{
                width: { xs: '100%', sm: 320 },
                // v3.90 - W3-1-Step3: amber accent for WOW insights
                borderLeft: `4px solid ${isWow ? '#F59E0B' : isLocked ? theme.palette.action.disabledBackground : config.color}`,
                outline: isWow ? '2px solid rgba(245,158,11,0.15)' : 'none',
                transition: 'box-shadow 0.2s',
                position: 'relative',
                '&:hover': { boxShadow: isLocked ? theme.shadows[2] : theme.shadows[4] },
              }}
            >
              {/* v3.86 - Lock overlay for free:false cards — plain text, no blur.
                  Numbers were already redacted server-side before this render. */}
              {isLocked && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'background.paper',
                    opacity: 0.92,
                    zIndex: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    px: 2,
                    textAlign: 'center',
                    borderRadius: 'inherit',
                  }}
                >
                  <LockIcon sx={{ color: 'text.disabled', fontSize: 32 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {rec.title}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {rec.body || 'Upgrade to unlock this insight.'}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<RocketLaunchIcon sx={{ fontSize: 14 }} />}
                    onClick={() => handleOpenUpgrade(rec.id)}
                    sx={{ fontWeight: 700, textTransform: 'none', mt: 0.5 }}
                  >
                    Unlock insight
                  </Button>
                </Box>
              )}

              <CardActionArea onClick={() => !isLocked && handleToggle(rec.id)} disabled={isLocked}>
                <CardContent sx={{ pb: isExpanded ? 0 : undefined }}>
                  {/* Header row: icon + type + impact */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ color: isWow ? '#F59E0B' : config.color, display: 'flex' }}>
                      {isWow ? <AutoAwesomeIcon /> : config.icon}
                    </Box>
                    {/* v3.90 - W3-1-Step3: WOW chip — label EXACTLY "WOW insight" per §4.16 */}
                    {isWow && (
                      <Chip
                        label="WOW insight"
                        size="small"
                        sx={{
                          bgcolor: '#F59E0B',
                          color: '#fff',
                          fontWeight: 700,
                          height: 22,
                          fontSize: '0.65rem',
                        }}
                      />
                    )}
                    <Chip
                      label={config.label}
                      size="small"
                      sx={{
                        bgcolor: `${config.color}15`,
                        color: config.color,
                        fontWeight: 500,
                        height: 22,
                      }}
                    />
                    <Chip
                      label={rec.impact}
                      size="small"
                      color={IMPACT_COLORS[rec.impact] || 'default'}
                      sx={{ height: 22, ml: 'auto' }}
                    />
                    {/* v3.29 - Confidence: only shown for free insights */}
                    {!isLocked && rec.confidence != null && rec.confidence > 0 && (
                      <Tooltip
                        title="How certain our AI is about this finding, based on the strength of the data pattern"
                        arrow
                        placement="top"
                      >
                        <Chip
                          label={`Confidence: ${Math.round(rec.confidence * 100)}%`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.65rem' }}
                        />
                      </Tooltip>
                    )}
                    {!isLocked && (
                      <ExpandMoreIcon
                        sx={{
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                          color: 'text.secondary',
                          fontSize: 20,
                        }}
                      />
                    )}
                  </Box>

                  {/* Title */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3, mb: 0.5 }}>
                    {rec.title}
                  </Typography>

                  {/* Dollar value label — only for free insights */}
                  {!isLocked && rec.estimatedValueLabel && (
                    <Typography
                      variant="body2"
                      sx={{ color: config.color, fontWeight: 700, mb: 0.5 }}
                    >
                      {rec.estimatedValueLabel}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>

              {/* Expandable detail section — free insights only */}
              {!isLocked && (
                <Collapse in={isExpanded}>
                  <CardContent sx={{ pt: 0 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      {rec.description}
                    </Typography>

                    {rec.methodology && (
                      <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 0.5 }}>
                        How: {rec.methodology}
                      </Typography>
                    )}

                    {rec.discoveryNeed && (
                      <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                        Based on: &quot;{rec.discoveryNeed}&quot;
                      </Typography>
                    )}

                    {/* v3.29 - Drill-down: open inline report dialog (fallback to Reports page) */}
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<AssessmentIcon sx={{ fontSize: 14 }} />}
                      onClick={(e) => handleDrillDown(e, rec)}
                      sx={{ mt: 1, textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      View related data
                    </Button>
                  </CardContent>
                </Collapse>
              )}
              {/* v3.77 - SHOW-01: "How we found this" expandable trace panel — free insights only */}
              {/* v3.90 - W3-1-Step3: pass wowSourceChunks for Level-2 WOW chunk render */}
              {!isLocked && (
                <ShowYourWork
                  entityType="recommendation"
                  entityId={rec.id}
                  wowSourceChunks={rec.wowSourceChunks}
                />
              )}
            </Card>
          );
        })}
      </Box>

      {/* v3.86 - Story 19 PLG: Upgrade modal — subscription offer first.
          Pay-per-question ($29) shown ONLY after user declines subscription (rejection path).
          These two offers must NEVER appear on the same screen at the same time. */}
      {/* v3.87 - Fix 1: onClose wired to handleDialogClose.
          Closing Screen 1 triggers Screen 2 if PAY_PER_QUESTION_ENABLED, otherwise closes.
          The two screens are never visible simultaneously. */}
      <Dialog
        open={upgradeModalOpen}
        onClose={handleDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {showPayPerQuestion ? 'Unlock just this insight' : 'Unlock all insights'}
        </DialogTitle>
        <DialogContent>
          {!showPayPerQuestion ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upgrade to MyLojic Pro to see all your AI insights, including the exact
                amounts, trends, and recommended actions from your real data.
              </Typography>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<RocketLaunchIcon />}
                onClick={handleUpgradeSubscription}
                disabled={upgradeLoading}
                sx={{ fontWeight: 700, textTransform: 'none', mb: 1 }}
              >
                {upgradeLoading ? <CircularProgress size={20} color="inherit" /> : upgradeCatalogTier ? `Upgrade to ${upgradeCatalogTier.display_name}: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: (upgradeCatalogTier.currency || 'usd').toUpperCase(), maximumFractionDigits: 0 }).format(upgradeCatalogTier.price_monthly)}/mo` : 'Upgrade to Pro'}
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Not ready to subscribe? Pay $29 to unlock just this one insight from your
                real data.
              </Typography>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handlePayPerQuestion}
                disabled={payPerQuestionLoading}
                sx={{ fontWeight: 700, textTransform: 'none', mb: 1 }}
              >
                {payPerQuestionLoading ? <CircularProgress size={20} color="inherit" /> : 'Unlock this insight: $29'}
              </Button>
            </>
          )}
        </DialogContent>
        {/* v3.87 - Fix 1: Screen 1 has no visible link to Screen 2.
            Screen 2 "No thanks" still allows a clean close. */}
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', alignItems: 'stretch', gap: 0.5 }}>
          {showPayPerQuestion && (
            <Button
              variant="text"
              size="small"
              onClick={() => { setUpgradeModalOpen(false); setShowPayPerQuestion(false); }}
              sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.75rem' }}
            >
              No thanks
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* v3.29 - Inline report viewer dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={() => { setReportDialogOpen(false); setSelectedReport(null); }}
        maxWidth="md"
        fullWidth
      >
        {selectedReport ? (
          <ReportViewer
            report={selectedReport}
            onClose={() => { setReportDialogOpen(false); setSelectedReport(null); }}
          />
        ) : (
          <>
            <DialogTitle>Report</DialogTitle>
            <DialogContent>
              <Typography>No report data available.</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setReportDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default InsightsPanel;
